import React from 'react';
import {
    Table,
    Button,
    Spinner,
    FormGroup,
} from 'reactstrap';
import moment from 'moment-timezone';
import { v4 as uuid } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes, faTrash, faScaleBalanced, faSave,
} from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import CustomForms from '../customForm/CustomForms';
import {
    FlexCenterRow,
    FlexColumnStart,
    FlexStartRow,
    SubHeading,
    GroupedRow,
    FormGroupColumn,
    FormLabel,
    Input,
    SmallButton,
    SmallOutlineButton,
    toasty,
    onFieldChange,
    Textarea,
} from '../common/forms/FormElements';
import { BaseFormViewModel } from '../common/ViewModel';
import SlideForm from '../common/forms/SlideForm';
import ValidatedSelect, { ReactSelectStyles } from '../common/forms/ValidatedSelect';
import FileUpload from '../common/forms/FileUpload';
import { WorkOrderNumberTypes } from '../contracts/Contract';
import CommonContext, { ApiRoutes, CustomFormContext } from '../Common';
import { ConfirmDelete, Modal } from '../common/Modal';
import OrganizationContactForm from '../organization/OrganizationContactForm';
import { OrganizationContact } from '../organization/Organization';
import AddressFormNew from '../address/AddressFormNew';
import { Address } from '../address/Address';
import { QuickSale, QuickSaleCharge } from './QuickSale';

const onFileDownload = (file) => {
    if (file?.id) {
        const url = ApiRoutes.fileUpload.getAttachmentById(file.id);
        httpClient.open(url, '_blank');
    }
};

// Replaces the contract's dispatch locations with the active set so inactive offices are not offered
// on new/edited quick sales. Pass quickSaleId only on the initial edit load to also keep the sale's
// already-selected office when it is now inactive; omit it when the contract changes (ROX-3953).
const applyQuickSaleDispatchLocations = async (contract, quickSaleId = null) => {
    if (!contract?.id) {
        return contract;
    }

    const dispatchLocations = await httpClient
        .js(ApiRoutes.typeAheads.dispatchCompaniesForQuickSale(contract.id, quickSaleId))
        .catch(() => null);

    if (dispatchLocations) {
        contract.dispatchLocations = dispatchLocations.map((x) => ({ id: x.value, companyName: x.label }));
    }

    return contract;
};

// Note: Contract select colors are now defined in QuickSaleSlideout.scss using CSS variables

export default class QuickSaleSlideout extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.organizationContactFormRef = React.createRef();
        this.subcontractorOrganizationContactFormRef = React.createRef();
        this.customFormsRef = React.createRef();
        this.addressFormRef = React.createRef();

        this.state = {
            customerOptions: [],
            loading: true,
            formValidated: false,
            existing: false,
            isValidated: false,
            saving: false,
            errors: {},
            quickSaleContract: null,
            quickSaleCompany: null,
            quickSaleCompanies: [],
            quickSaleId: null,
            contractsLoading: false,
            contractLoading: false,
            showContactsForm: false,
            showSubcontractorContactsForm: false,
            selectedContact: null,
            selectedSubcontractorContact: null,
            organizationContacts: [],
            subcontractorContacts: [],
            quickSale: new QuickSale(),
            sessionId: `quickSale-${uuid()}`,
            customForms: [],
            ...new BaseFormViewModel(),
        };
        this.onChange = this.onChange.bind(this);
    }

    open = (model) => {
        this.setState({
            loading: true,
            formValidated: false,
            quickSaleId: model?.id ?? null,
            quickSaleCompany: null,
            quickSaleContract: null,
            contractLoading: false,
            contractsLoading: false,
            selectedContact: null,
            selectedSubcontractorContact: null,
            organizationContacts: [],
            subcontractorContacts: [],
            customForms: [],
        }, () => this.populateState());
    };

    populateState = async () => {
        const { quickSaleId } = this.state;
        const quickSaleCompanies = await httpClient.get(
            ApiRoutes.quickSale.companies(),
        );

        if (quickSaleId) {
            const quickSale = await httpClient.get(ApiRoutes.quickSale.byId(quickSaleId));

            const [quickSaleContracts, quickSaleContract, contacts, customForms] =
                await Promise.all([
                    httpClient.get(ApiRoutes.quickSale.contracts(quickSale.companyId, quickSaleId)),
                    httpClient.get(ApiRoutes.quickSale.contract(quickSale.contractId)),
                    httpClient.get(ApiRoutes.company.contacts(quickSale.companyId)),
                    httpClient.post(ApiRoutes.customForm.forContext(), { companyId: quickSale.companyId, formContextId: CustomFormContext.QuickSale }),
                ]);

            const quickSaleCompany = quickSaleCompanies.find((c) => c.id === quickSale.companyId);

            if (contacts) {
                const organizationContacts = contacts.map((c) => ({
                    label: c.fullName,
                    value: c.id,

                }));
                this.setState({ organizationContacts });
            }

            if (quickSale.subcontractorId) {
                const subcontractorContacts = await httpClient.get(ApiRoutes.company.contacts(quickSale.subcontractorId));

                if (subcontractorContacts) {
                    const subcontractorContactsMapped = subcontractorContacts.map((c) => ({
                        label: c.fullName,
                        value: c.id,
                    }));
                    this.setState({ subcontractorContacts: subcontractorContactsMapped });
                }
            }

            await applyQuickSaleDispatchLocations(quickSaleContract, quickSaleId);

            this.setState({
                quickSaleCompanies, quickSaleCompany, quickSaleContract, quickSaleContracts, loading: false, quickSale, customForms,
            }, () => this.customFormsRef.current?.loadForms());
        } else {
            const newQuickSale = new QuickSale();
            this.setState({ quickSaleCompanies, loading: false, quickSale: newQuickSale });
        }
    };

    onSubContractorChanged = async (option) => {
        const { quickSale } = this.state;
        let subcontractorContacts = [];

        if (!option?.id) {
            this.setState({
                quickSale: {
                    ...quickSale, subcontractorId: null, subcontractorContactId: null,
                },
                subcontractorContacts,
            });
            return;
        }

        const contacts = await httpClient.get(ApiRoutes.company.contacts(option.id));

        if (contacts) {
            subcontractorContacts = contacts.map((c) => ({
                label: c.fullName,
                value: c.id,
            }));
        }

        const updatedQuickSale = {
            ...quickSale,
            subcontractorId: option ? option.id : null,
        };
        this.setState({ quickSale: updatedQuickSale, subcontractorContacts });
    };

    onCustomerChanged = (option) => {
        this.setState({ contractsLoading: true });

        this.setState((prevState) => ({
            selectedContact: null,
            selectedSubcontractorContact: null,
            organizationContacts: [],
            subcontractorContacts: [],
            quickSale: {
                ...prevState.quickSale,
                companyId: null,
                dispatchOfficeId: null,
                dispatchOffice: null,
                project: null,
            },
            quickSaleCompany: null,
            customForms: null,
        }), async () => {
            this.clearContract();

            if (!option) {
                this.setState({
                    contractsLoading: false,
                });
                return;
            }

            let organizationContacts = [];
            const [quickSaleContracts, contacts, customForms] = await Promise.all(
                [
                    httpClient.get(ApiRoutes.quickSale.contracts(option.id)),
                    httpClient.get(ApiRoutes.company.contacts(option.id)),
                    httpClient.post(ApiRoutes.customForm.forContext(), { companyId: option.id, formContextId: CustomFormContext.QuickSale }),
                ],
            );

            if (contacts) {
                organizationContacts = contacts.map((c) => ({
                    label: c.fullName,
                    value: c.id,
                }));
            }

            this.setState((prevState) => ({
                quickSale: {
                    ...prevState.quickSale,
                    companyId: option.id,
                },
                contractsLoading: false,
                quickSaleContracts,
                quickSaleCompany: option,
                organizationContacts,
                customForms,
            }));
        });
    };

    clearContract = () => {
        const { quickSale } = this.state;
        this.setState({
            quickSale: {
                ...quickSale,
                contractId: null,
                contractNumber: null,
                contractDescription: null,
                requestedById: null,
                subcontractorId: null,
                subcontractorContactId: null,
                dispatchOfficeId: null,
                dispatchOffice: null,
                project: null,
            },
            quickSaleContract: null,
        });
    };

    onContractChanged = async (option) => {
        const { quickSale } = this.state;
        this.clearContract();

        if (!option) {
            return;
        }

        this.setState({ contractLoading: true });
        const selectedContract = await httpClient.get(ApiRoutes.quickSale.contract(option.id));

        await applyQuickSaleDispatchLocations(selectedContract);

        this.setState({
            quickSale: {
                ...quickSale,
                contractId: option.id,
                contractNumber: selectedContract?.number ?? '',
                contractDescription: selectedContract?.description ?? '',
            },
            quickSaleContract: selectedContract,
            contractLoading: false,
        });
    };

    onProjectChanged = async (option) => {
        const { quickSale, quickSaleContract } = this.state;

        if (!option) {
            this.setState({
                quickSale: {
                    ...quickSale,
                    projectId: null,
                    project: null,
                    dispatchOfficeId: null,
                    dispatchOffice: null,
                },
            });
        }

        if (option) {
            const project = quickSaleContract.projects.find((p) => p.id === option.id);
            const dispatchOffice = quickSaleContract.dispatchLocations.find((d) => d.id === project?.dispatchCompanyId);

            if (project && dispatchOffice) {
                const updatedQuickSale = {
                    ...quickSale,
                    projectId: option.id,
                    project: option,
                    dispatchOfficeId: dispatchOffice?.id,
                    dispatchOffice,
                    workOrderNumber: option.workOrderNumber,
                    purchaseOrderNumber: option.purchaseOrderNumber,
                    workRequest: option.workRequest,
                };
                this.setState({ quickSale: updatedQuickSale });

                if (project.jobLocation) {
                    const { jobLocation } = project;
                    const usePrefill = await Modal.ask({
                        title: 'Use Project Location?',
                        body: `Set the Quick Sale location to the project's location: ${jobLocation.address1} ${jobLocation.city}, ${jobLocation.state} ${jobLocation.zip}?`,
                        SaveButtonProps: { label: 'Use Project Location' },
                    });

                    if (usePrefill) {
                        this.setState((prevState) => ({
                            quickSale: {
                                ...prevState.quickSale,
                                location: { ...jobLocation, id: null },
                                locationId: null,
                            },
                        }));
                    }
                }
            }
        }
    };

    onChange = onFieldChange;

    onDispatchLocationChanged = (option) => {
        const { quickSale } = this.state;
        const updatedQuickSale = {
            ...quickSale,
            dispatchOfficeId: option ? option.id : null,
            dispatchOffice: option,
        };
        this.setState({ quickSale: updatedQuickSale });
    };

    onAddLocation = () => {
        const { quickSaleContract } = this.state;
        const address = new Address(quickSaleContract?.countryId, quickSaleContract?.countryCode);
        this.addressFormRef.current.open(address, 'New Location');
    };

    onEditLocation = () => {
        const { quickSale } = this.state;
        this.addressFormRef.current.open({ ...quickSale.location }, 'Edit Location');
    };

    onLocationSaved = (address) => {
        const { quickSale } = this.state;
        this.setState({
            quickSale: { ...quickSale, location: { ...address, id: null }, locationId: null },
        });
    };

    onRemoveLocation = () => {
        const { quickSale } = this.state;
        this.setState({ quickSale: { ...quickSale, location: null, locationId: null } });
    };

    static getContact = async (contactId) => await httpClient
        .js(ApiRoutes.locationContact.byId(contactId))
        .catch(() => {
            console.log('Unable to get billing contacts');
        });

    onContactChanged = (option, isSubcontractor) => {
        const { quickSale } = this.state;

        let updatedQuickSale = null;

        if (isSubcontractor) {
            updatedQuickSale = {
                ...quickSale,
                subcontractorContactId: option ? option.value : null,
            };
        } else {
            updatedQuickSale = {
                ...quickSale,
                requestedById: option ? option.value : null,
            };
        }
        this.setState({ quickSale: updatedQuickSale });
    };

    onEditContact = async (contactId, isSubcontractor, readOnly = false) => {
        const contact = await QuickSaleSlideout.getContact(contactId);

        this.setState({ contactFormIsReadOnly: readOnly });

        if (isSubcontractor) {
            this.setState({
                showSubcontractorContactsForm: true,
                showContactsForm: false,
                selectedSubcontractorContact: contact,
                selectedContact: null,
            });
        } else {
            this.setState({
                showContactsForm: true,
                showSubcontractorContactsForm: false,
                selectedContact: contact,
                selectedSubcontractorContact: null,
            });
        }
    };

    onAddContact = (isSubcontractor) => {
        const { quickSale } = { ...this.state };
        const selectedContact = {
            ...new OrganizationContact(),
            id: null,
            companyId: isSubcontractor ? quickSale?.subcontractorId : quickSale?.companyId,
        };

        if (isSubcontractor) {
            this.setState({ showSubcontractorContactsForm: true, selectedSubcontractorContact: selectedContact });
        } else {
            this.setState({ showContactsForm: true, selectedContact });
        }
    };

    onOrganizationContactSaved = async (isSubcontractor) => {
        this.setState({ contactFormIsReadOnly: true });

        const {
            selectedContact,
            organizationContacts,
            selectedSubcontractorContact,
            subcontractorContacts,
        } = {
            ...this.state,
        };

        const contact = isSubcontractor ? selectedSubcontractorContact : selectedContact;
        const selected = isSubcontractor ? selectedSubcontractorContact : selectedContact;

        const isNew = parseInt(contact.id ?? 0) <= 0;
        const result = await httpClient.andGetResponse(
            isNew ? httpClient.types.post : httpClient.types.put,
            isNew
                ? ApiRoutes.locationContact.create()
                : ApiRoutes.locationContact.update(contact.id),
            { ...selected },
            'Error Saving Contact',
        );

        if (result) {
            toasty.success('Contact saved.');

            if (isNew) {
                if (isSubcontractor) {
                    subcontractorContacts.push({
                        label: `${selected.firstName} ${selected.lastName}`,
                        value: result,
                    });
                } else {
                    organizationContacts.push({
                        label: `${selected.firstName} ${selected.lastName}`,
                        value: result,
                    });
                }

                const { quickSale } = this.state;
                const updatedQuickSale = {
                    ...quickSale,
                    requestedById: isSubcontractor ? quickSale.requestedById : (result || null),
                    subcontractorContactId: isSubcontractor ? result : quickSale.subcontractorContactId,
                };
                this.setState({ quickSale: updatedQuickSale });
            } else {
                if (isSubcontractor) {
                    const existingContactIndex = subcontractorContacts.findIndex((c) => c.value === selected?.id);
                    if (existingContactIndex > -1) {
                        subcontractorContacts[existingContactIndex].label = `${selected.firstName} ${selected.lastName}`;
                    }
                } else {
                    const existingContactIndex = organizationContacts.findIndex((c) => c.value === selected?.id);
                    if (existingContactIndex > -1) {
                        organizationContacts[existingContactIndex].label = `${selected.firstName} ${selected.lastName}`;
                    }
                }
            }

            this.setState({
                showContactsForm: false,
                showSubcontractorContactsForm: false,
                saving: false,
                organizationContacts,
                subcontractorContacts,
            });

            this.setState({ contactFormIsReadOnly: false });
            this.organizationContactFormRef.current.resetForm();
            this.subcontractorOrganizationContactFormRef.current.resetForm();
            this.setState({ selectedContact: null, selectedSubcontractorContact: null });
        } else {
            this.setState({
                saving: false,
            });
        }
    };

    onSubmit = async () => {
        const { quickSale, sessionId } = this.state;
        const hasChargesWithQuantity = quickSale?.charges?.some((charge) => charge.qty > 0);
        if (!hasChargesWithQuantity) {
            toasty.error('At least one charge must have a Quantity');
            return;
        }

        this.setState({ saving: true, errors: {} });

        const quickSaleToSubmit = {
            ...quickSale,
            saleDateTime: moment(quickSale.saleDateTime).format(),
            charges: quickSale.charges?.map((charge) => ({
                ...charge,
                billingDetailId: typeof charge.billingDetailId === 'string' ? null : charge.billingDetailId,
            })),
            location: quickSale.location
                ? {
                    ...quickSale.location,
                    id: typeof quickSale.location.id === 'string' ? null : quickSale.location.id,
                }
                : null,
        };

        try {
            if (quickSale.id) {
                const response = await httpClient.put(ApiRoutes.quickSale.update(quickSale.id), quickSaleToSubmit);
                if (response) {
                    toasty.success('QuickSale updated successfully');
                    this.setState({ quickSale: response, saving: false });
                }
            } else {
                quickSaleToSubmit.sessionId = sessionId;
                const response = await httpClient.post(ApiRoutes.quickSale.create(), quickSaleToSubmit);
                if (response) {
                    toasty.success('QuickSale created successfully');
                    this.setState({ quickSale: response, saving: false });
                }
            }
        } catch {
            toasty.error('Could not create QuickSale');
            this.setState({ saving: false });
        }
    };

    onQuantityChange = (chargeId, quantity) => {
        const { quickSale } = this.state;
        if (quickSale && quickSale.charges) {
            const updatedCharges = quickSale.charges.map((charge) =>
                charge.billingDetailId === chargeId
                    ? { ...charge, qty: quantity }
                    : charge,
            );

            this.setState({
                quickSale: {
                    ...quickSale,
                    charges: updatedCharges,
                },
            });
        }
    };

    onCustomDiscriptionChange = (chargeId, description) => {
        const { quickSale } = this.state;
        if (quickSale && quickSale.charges) {
            const updatedCharges = quickSale.charges.map((charge) =>
                charge.billingDetailId === chargeId
                    ? { ...charge, customDescription: description }
                    : charge,
            );
            this.setState({
                quickSale: {
                    ...quickSale,
                    charges: updatedCharges,
                },
            });
        }
    };

    onChargeChanged = (chargeId, option) => {
        const { quickSale } = this.state;
        if (quickSale && quickSale.charges) {
            const updatedCharges = quickSale.charges.map((charge) => {
                if (charge.billingDetailId === chargeId) {
                    if (option) {
                        const { equipments, ...optionWithoutEquipments } = option;
                        return { ...optionWithoutEquipments, billingDetailId: charge.billingDetailId };
                    } else {
                        return { ...new QuickSaleCharge(), billingDetailId: charge.billingDetailId };
                    }
                }
                return charge;
            });

            this.setState({
                quickSale: {
                    ...quickSale,
                    charges: updatedCharges,
                },
            });
        }
    };

    onItemChange = (chargeId, option) => {
        const { quickSale } = this.state;
        if (quickSale && quickSale.charges) {
            const updatedCharges = quickSale.charges.map((charge) =>
                charge.billingDetailId === chargeId
                    ? {
                        ...charge,
                        equipmentId: option ? option.id : null,
                        equipmentDescription: option ? option.description : '',
                        customDescription: '',
                    }
                    : charge,
            );

            this.setState({
                quickSale: {
                    ...quickSale,
                    charges: updatedCharges,
                },
            });
        }
    };

    onFileUploadComplete = (chargeId, uploadedFile) => {
        const { quickSale } = this.state;
        if (quickSale && quickSale.charges && uploadedFile) {
            const updatedCharges = quickSale.charges.map((charge) =>
                charge.billingDetailId === chargeId
                    ? {
                        ...charge,
                        attachments: [...(charge.attachments || []), uploadedFile],
                    }
                    : charge,
            );

            this.setState({
                quickSale: {
                    ...quickSale,
                    charges: updatedCharges,
                },
            });
        }
    };

    onFileDelete = (chargeId, fileToDelete) => {
        const { quickSale } = this.state;
        if (quickSale && quickSale.charges) {
            const updatedCharges = quickSale.charges.map((charge) =>
                charge.billingDetailId === chargeId
                    ? {
                        ...charge,
                        attachments: charge.attachments.filter((file) => file.id !== fileToDelete.id),
                    }
                    : charge,
            );

            this.setState({
                quickSale: {
                    ...quickSale,
                    charges: updatedCharges,
                },
            });
        }
    };

    onClearErrors = () => {
        this.setState({ errors: {} });
    };

    addNewCharge = () => {
        const { quickSale } = this.state;
        if (quickSale) {
            const newCharge = new QuickSaleCharge();

            const updatedCharges = [...(quickSale.charges || []), newCharge];

            this.setState({
                quickSale: {
                    ...quickSale,
                    charges: updatedCharges,
                },
            });
        }
    };

    deleteCharge = (chargeId) => {
        const { quickSale } = this.state;
        if (quickSale && quickSale.charges) {
            const updatedCharges = quickSale.charges.filter((charge) => charge.billingDetailId !== chargeId);

            this.setState({
                quickSale: {
                    ...quickSale,
                    charges: updatedCharges,
                },
            });
        }
    };

    resetForm = () => {
        this.setState({
            formValidated: false,
            saving: false,
        });
    };

    onClose = () => {
        this.resetForm();
        this.props.toggleShow(false);
        this.props.onClose();
    };

    onDelete = async () => {
        this.setState({ loading: true });
        const { quickSale } = this.state;

        const response = await httpClient.delete(ApiRoutes.quickSale.delete(quickSale.id)).catch();
        this.setState({ loading: false });

        if (response) {
            toasty.success('QuickSale deleted successfully');
            this.onClose();
        } else {
            toasty.error('Could not delete QuickSale');
        }
    };

    showConfirmDeleteModal = () => {
        ConfirmDelete.show({
            title: 'Delete Quick Sale',
            onSubmit: () => this.onDelete(),
            body: 'Are you sure you want to delete this Quick Sale?',
        });
    };

    render() {
        const {
            saving,
            errors,
            formValidated,
            loading,
            quickSale,
            quickSaleCompanies,
            quickSaleCompany,
            quickSaleContracts,
            quickSaleContract,
            validationMessage,
            contractsLoading,
            contractLoading,
            sessionId,
            customForms,
        } = this.state;

        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};
        const useProjects = tenantSettings.useContractProjects === 2
            || (tenantSettings.useContractProjects === 1 && tenantSettings.allowRentalChargeTypes);

        return (
            <>
                <SlideForm
                    size="col-xl-12 col-md-12 col-xs-12"
                    loading={loading || !tenantSettings}
                    show={this.props.show}
                    id="quickSaleForm"
                    formIcon={faScaleBalanced}
                    formTitle="Quick Sale"
                    ref={this.formRef}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    fullScreen
                    isValidated={formValidated}
                    className="pb-2 w-100"
                    onSubmit={this.onSubmit}
                    onClose={this.onClose}
                    errors={errors}
                    onClearErrors={this.onClearErrors}
                    error={validationMessage}
                    readOnly={!!saving}
                    hideToolbar
                >
                    <SubHeading>Customer Details</SubHeading>
                    <GroupedRow>
                        <FormGroupColumn>
                            <ValidatedSelect
                                id="companyId"
                                name="quickSale.companyId"
                                label="Customer"
                                options={quickSaleCompanies}
                                value={
                                    (quickSaleCompanies ?? []).find(
                                        (x) => x.id === quickSale?.companyId,
                                    ) ?? ''
                                }
                                getOptionLabel={(o) => o.companyName}
                                getOptionValue={(o) => o.id}
                                onChange={this.onCustomerChanged}
                                isDisabled={quickSale?.isReadOnly || saving}
                                required
                                error="A customer selection is required."
                            />

                            <ValidatedSelect
                                id="contractId"
                                name="quickSale.contractId"
                                className="quick-sale-contract-select"
                                label={contractsLoading ? (
                                    <Spinner
                                        size="sm"
                                        className="ml-2"
                                        style={{ marginTop: '-10px' }}
                                    />
                                ) : 'Contract'}
                                options={quickSaleContracts}
                                value={
                                    (quickSaleContracts ?? []).find(
                                        (x) => x.id === quickSale?.contractId,
                                    ) ?? ''
                                }
                                getOptionLabel={(o) => o.number}
                                getOptionValue={(o) => o.id}
                                styles={ReactSelectStyles}
                                formatOptionLabel={(opt, { context }) => {
                                    if (context === 'menu') {
                                        return (
                                            <FlexColumnStart>
                                                <span>{opt.number}</span>
                                                {Boolean(opt.contractDescription) && (
                                                    <small className=" mt-1">
                                                        {opt.contractDescription}
                                                    </small>
                                                )}
                                            </FlexColumnStart>
                                        );
                                    }

                                    return <span>{opt.number}</span>;
                                }}
                                onChange={this.onContractChanged}
                                isDisabled={!quickSale?.companyId || quickSale?.isReadOnly || saving || contractsLoading}
                                required
                                error="A contract selection is required."
                            />

                            {useProjects && quickSaleContract?.isProjectContract === true && (
                                <ValidatedSelect
                                    id="projectId"
                                    name="quickSale.projectId"
                                    label="Project"
                                    options={quickSaleContract?.projects ?? []}
                                    value={quickSale?.project}
                                    getOptionLabel={(o) => o.projectNumber}
                                    getOptionValue={(o) => o.id}
                                    onChange={this.onProjectChanged}
                                    placeholder="Select project"
                                    required={quickSaleContract?.requiresProjectForQuickSale}
                                    isClearable
                                    isDisabled={!quickSale?.contractId || quickSale?.isReadOnly || saving}
                                />
                            )}

                            <ValidatedSelect
                                id="dispatchOfficeId"
                                name="quickSale.dispatchOfficeId"
                                label={contractLoading ? (
                                    <Spinner
                                        size="sm"
                                        className="ml-2"
                                        style={{ marginTop: '-10px' }}
                                    />
                                ) : 'Dispatch Office'}
                                options={quickSaleContract?.dispatchLocations ?? []}
                                value={quickSale?.dispatchOffice}
                                getOptionLabel={(o) => o.companyName}
                                getOptionValue={(o) => o.id}
                                onChange={this.onDispatchLocationChanged}
                                placeholder="Select dispatch location..."
                                isClearable
                                isDisabled={!quickSale?.contractId || quickSale?.isReadOnly || saving || quickSale?.projectId || contractLoading}
                                required
                                error="A dispatch location selection is required."
                            />

                            <ValidatedSelect
                                label="Requested By"
                                showAddButton={!(!quickSale?.contractId || quickSale?.isReadOnly || saving || quickSale?.projectId || contractLoading)}
                                showEditButton={!!quickSale.requestedById}
                                onEditButtonClick={() => this.onEditContact(quickSale.requestedById, false)}
                                onAddButtonClick={() => this.onAddContact(false)}
                                showViewButton={!!quickSale.requestedById}
                                onViewButtonClick={() => this.onEditContact(quickSale.requestedById, false, true)}
                                id="requestedById"
                                name="quickSale.requestedById"
                                options={
                                    this.state.organizationContacts ?? []
                                }
                                value={
                                    (this.state.organizationContacts || []).find(
                                        (x) => x.value === quickSale?.requestedById,
                                    ) ?? null
                                }
                                getOptionLabel={(o) => o.label}
                                getOptionValue={(o) => o.value}
                                onChange={(option) => this.onContactChanged(option, false)}
                                placeholder="Select contact..."
                                isClearable
                                isDisabled={!quickSale?.contractId || quickSale?.isReadOnly || saving}
                                required
                                error="A contact selection is required."
                            />

                            <Input
                                label="Sale Time"
                                id="saleDateTime"
                                required
                                type="datetime-local"
                                name="quickSale.saleDateTime"
                                disabled={quickSale?.isReadOnly}
                                value={moment(quickSale.saleDateTime).format('YYYY-MM-DDTHH:mm') ?? ''}
                                onChange={this.onChange}
                                error="Date is required"
                            />
                            {customForms?.length > 0 && (
                                <CustomForms
                                    ref={this.customFormsRef}
                                    tooltipId="qs-forms-incomplete-icon"
                                    disabled={quickSale?.isReadOnly}
                                    quickSaleId={quickSale?.id || null}
                                    formDate={quickSale.saleDateTime}
                                    companyId={quickSaleCompany?.id}
                                    readOnly={quickSale?.isReadOnly}
                                    sessionId={sessionId}
                                    formContextId={CustomFormContext.QuickSale}
                                />
                            )}
                        </FormGroupColumn>

                        <FormGroupColumn>
                            {(quickSaleCompany ?? {}).subcontractors?.length > 0 && (
                                <>
                                    <ValidatedSelect
                                        id="subcontractorId"
                                        name="quickSale.subcontractorId"
                                        label="Subcontractor"
                                        options={
                                            quickSaleCompany?.subcontractors ?? []
                                        }
                                        value={
                                            (quickSaleCompany?.subcontractors || []).find(
                                                (x) => x.id === quickSale?.subcontractorId,
                                            ) ?? null
                                        }
                                        isDisabled={quickSale?.isReadOnly || saving}
                                        getOptionLabel={(o) => o.companyName}
                                        getOptionValue={(o) => o.id}
                                        required
                                        onChange={this.onSubContractorChanged}
                                        placeholder="Select Subcontractor..."
                                        isClearable
                                        error="A subcontractor selection is required."
                                    />

                                    <ValidatedSelect
                                        label="Subcontractor Contact"
                                        entityName="Contact"
                                        showAddButton={!(!quickSale?.subcontractorId || quickSale?.isReadOnly || saving || quickSale?.projectId || contractLoading)}
                                        showEditButton={!!quickSale.subcontractorContactId}
                                        onEditButtonClick={() => this.onEditContact(quickSale.subcontractorContactId, true)}
                                        onAddButtonClick={() => this.onAddContact(true)}
                                        showViewButton={!!quickSale.subcontractorContactId}
                                        onViewButtonClick={() => this.onEditContact(quickSale.subcontractorContactId, true, true)}
                                        id="subcontractorContactId"
                                        name="quickSale.subcontractorContactId"
                                        options={
                                            this.state.subcontractorContacts ?? []
                                        }
                                        value={
                                            (this.state.subcontractorContacts || []).find(
                                                (x) => x.value === quickSale?.subcontractorContactId,
                                            ) ?? null
                                        }
                                        getOptionLabel={(o) => o.label}
                                        getOptionValue={(o) => o.value}
                                        onChange={(option) => this.onContactChanged(option, true)}
                                        placeholder="Select subcontractor contact..."
                                        isClearable
                                        isDisabled={!quickSale?.subcontractorId || quickSale?.isReadOnly || saving}
                                        required
                                        error="A contact selection is required."
                                    />
                                </>
                            )}

                            {(quickSaleContract?.workOrderTypeId === WorkOrderNumberTypes.Optional
                                || quickSaleContract?.workOrderTypeId === WorkOrderNumberTypes.Required) && (
                                    <Input
                                        id="workOrderNumber"
                                        name="quickSale.workOrderNumber"
                                        label="Work Order Number"
                                        value={(quickSale ?? {}).workOrderNumber ?? ''}
                                        disabled={quickSale?.isReadOnly}
                                        required={quickSaleContract?.workOrderTypeId === WorkOrderNumberTypes.Required}
                                        onChange={this.onChange}
                                    />
                                )}
                            {quickSaleCompany?.invoiceHasPO && (
                                <Input
                                    id="purchaseOrderNumber"
                                    name="quickSale.purchaseOrderNumber"
                                    label="PO Number"
                                    value={(quickSale ?? {}).purchaseOrderNumber ?? ''}
                                    disabled={quickSale?.isReadOnly}
                                    onChange={this.onChange}
                                />
                            )}
                            {quickSaleCompany?.invoiceUsesWorkRequest && (
                                <Input
                                    id="workRequest"
                                    name="quickSale.workRequest"
                                    label="Work Request"
                                    value={(quickSale ?? {}).workRequest ?? ''}
                                    disabled={quickSale?.isReadOnly}
                                    onChange={this.onChange}
                                />
                            )}
                            <Textarea
                                label="Notes"
                                id="notes"
                                name="quickSale.notes"
                                value={quickSale.notes ?? ''}
                                onChange={this.onChange}
                                placeholder="Enter notes to field staff here."
                                maxLength="1000"
                                rows="5"
                                disabled={quickSale?.isReadOnly}
                            />
                            <FormGroup>
                                <FormLabel
                                    htmlFor="location"
                                    label="Location"
                                />
                                {quickSale?.location ? (
                                    <FlexStartRow>
                                        <SmallOutlineButton
                                            onClick={this.onEditLocation}
                                            label="Edit"
                                            disabled={quickSale?.isReadOnly || saving}
                                        />
                                        <SmallOutlineButton
                                            className="ml-2"
                                            onClick={this.onRemoveLocation}
                                            label="Remove"
                                            disabled={quickSale?.isReadOnly || saving}
                                        />
                                        <span className="form-control-plaintext ml-2">
                                            {quickSale.location.address1}
                                            {' '}
                                            {quickSale.location.city}
                                            {', '}
                                            {quickSale.location.state}
                                            {' '}
                                            {quickSale.location.zip}
                                        </span>
                                    </FlexStartRow>
                                ) : (
                                    <div>
                                        <Button
                                            type="button"
                                            color="secondary"
                                            size="sm"
                                            disabled={!quickSale?.contractId || quickSale?.isReadOnly || saving}
                                            onClick={this.onAddLocation}
                                        >
                                            Add Location
                                        </Button>
                                    </div>
                                )}
                            </FormGroup>
                        </FormGroupColumn>
                    </GroupedRow>

                    <div className="d-flex justify-content-center align-items-center mb-3 mt-3 position-relative">
                        <SubHeading>Charges</SubHeading>
                        <SmallButton
                            color="outline-primary"
                            disabled={!quickSale?.contractId}
                            onClick={this.addNewCharge}
                            className="floating ml-auto mr-0 mt-3"
                        >
                            <i className="fa fa-plus-circle fa-md mr-2" />
                            Add Charge
                        </SmallButton>
                    </div>
                    <div>
                        <Table
                            responsive
                            className="mb-5"
                        >
                            <colgroup>
                                <col style={{ width: '20%' }} />
                                <col style={{ width: '20%' }} />
                                <col style={{ width: '25%' }} />
                                <col style={{ width: '5%' }} />
                                <col style={{ width: '5%' }} />
                                <col style={{ width: '20%' }} />
                                <col style={{ width: '5%' }} />
                            </colgroup>
                            <thead>
                                <tr className="text-center">
                                    <th>Charge</th>
                                    <th>Item</th>
                                    <th>Description</th>
                                    <th>Units</th>
                                    <th>Quantity</th>
                                    <th>Attachments</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {quickSale?.charges?.length > 0
                                    && (quickSale.charges.map((charge) => {
                                        const selectedCharge = (quickSaleContract?.chargeTypes || []).find((x) => x.chargeTypeId === charge.chargeTypeId) || null;
                                        return (
                                            <tr key={charge.billingDetailId}>
                                                <td>
                                                    <ValidatedSelect
                                                        id={`chargeDescription_${charge.billingDetailId}`}
                                                        name={`quickSale.charges[${charge.billingDetailId}].chargeTypeId`}
                                                        options={quickSaleContract?.chargeTypes || []}
                                                        value={selectedCharge}
                                                        getOptionLabel={(o) => o.description}
                                                        getOptionValue={(o) => o.chargeTypeId}
                                                        onChange={(option) => this.onChargeChanged(charge.billingDetailId, option)}
                                                        placeholder="Select charge type..."
                                                        isClearable
                                                        isDisabled={quickSale?.isReadOnly || saving}
                                                        menuPortalTarget={document.body}
                                                        styles={{
                                                            menuPortal: (base) => ({ ...base, zIndex: 10001 }),
                                                            menu: (base) => ({ ...base, zIndex: 10001 }),
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    {selectedCharge?.equipments?.length > 0 && (
                                                        <ValidatedSelect
                                                            id={`chargeItem_${charge.billingDetailId}`}
                                                            name={`quickSale.charges[${charge.billingDetailId}].equipmentId`}
                                                            options={selectedCharge.equipments}
                                                            value={
                                                                selectedCharge.equipments.find(
                                                                    (x) => x.id === charge.equipmentId,
                                                                ) || null
                                                            }
                                                            getOptionLabel={(o) => o.description}
                                                            getOptionValue={(o) => o.id}
                                                            onChange={(option) => this.onItemChange(charge.billingDetailId, option)}
                                                            placeholder="Select equipment..."
                                                            isClearable
                                                            isDisabled={!charge.chargeTypeId || quickSale?.isReadOnly || saving}
                                                            menuPortalTarget={document.body}
                                                            styles={{
                                                                menuPortal: (base) => ({ ...base, zIndex: 10001 }),
                                                                menu: (base) => ({ ...base, zIndex: 10001 }),
                                                            }}
                                                        />
                                                    )}
                                                </td>
                                                <td>
                                                    <Input
                                                        type="text"
                                                        value={charge.customDescription ?? ''}
                                                        disabled={quickSale?.isReadOnly}
                                                        onChange={(e) => this.onCustomDiscriptionChange(charge.billingDetailId, e.target.value || '')}
                                                    />
                                                </td>
                                                <td className="text-center">
                                                    <div className="mt-2">{charge.units}</div>
                                                </td>
                                                <td>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        step="1"
                                                        value={charge.qty ?? 0}
                                                        disabled={quickSale?.isReadOnly}
                                                        onChange={(e) => this.onQuantityChange(charge.billingDetailId, parseFloat(e.target.value) || 0)}
                                                        style={{ width: '80px', margin: '0 auto' }}
                                                    />
                                                </td>
                                                <td className="text-center">
                                                    {charge.allowAttachments && (
                                                        <div
                                                            className="d-flex justify-content-center"
                                                            style={{ minWidth: '100px' }}
                                                        >
                                                            <FileUpload
                                                                disabled={quickSale?.isReadOnly}
                                                                url={ApiRoutes.fileUpload.createAttachment()}
                                                                accept={['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.doc', '.docx', '.xls', '.xlsx']}
                                                                files={charge.attachments || []}
                                                                onUploadComplete={(uploadedFile) => this.onFileUploadComplete(charge.billingDetailId, uploadedFile)}
                                                                onDownload={onFileDownload}
                                                                onDelete={(file) => this.onFileDelete(charge.billingDetailId, file)}
                                                                type="simple"
                                                            />
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="text-center mt-2">
                                                    <SmallButton
                                                        disabled={quickSale?.isReadOnly}
                                                        onClick={() => this.deleteCharge(charge.billingDetailId)}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faTimes}
                                                            color="text-primary"
                                                            className="mr-1"
                                                        />
                                                    </SmallButton>
                                                </td>
                                            </tr>
                                        );
                                    },
                                    ))}
                            </tbody>
                        </Table>
                    </div>
                    <FlexCenterRow className="pt-5">
                        {!quickSale?.isReadOnly && (
                            <Button
                                type="submit"
                                color="primary"
                                disabled={saving || !quickSale?.contractId}
                                className="me-2"
                            >
                                <FontAwesomeIcon icon={faSave} />
                                <span className="ml-2">{saving ? 'Saving...' : 'Save '}</span>
                            </Button>
                        )}
                        <Button
                            type="button"
                            color="secondary"
                            className="ml-2"
                            onClick={this.onClose}
                        >
                            Close
                        </Button>
                        {(quickSale?.id !== null && !quickSale?.isReadOnly) && (
                            <Button
                                type="button"
                                color="danger"
                                className="ml-2"
                                onClick={this.showConfirmDeleteModal}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                <span className="ml-2">Delete</span>
                            </Button>
                        )}
                    </FlexCenterRow>
                </SlideForm>
                <OrganizationContactForm
                    ref={this.organizationContactFormRef}
                    show={this.state.showContactsForm}
                    contact={this.state.selectedContact}
                    readOnly={this.state.contactFormIsReadOnly}
                    allowContactTypeEdit={!quickSale.requestedById}
                    allowAllContactTypes
                    id="organizationContactForm"
                    isDisabled={!quickSale?.contractId || quickSale?.isReadOnly || saving}
                    onClose={() => {
                        this.setState({ showContactsForm: false, contactFormIsReadOnly: false });
                        this.organizationContactFormRef.current.resetForm();
                        this.setState({ selectedContact: null });
                    }}
                    onChange={(e) => {
                        const { selectedContact } = this.state;
                        selectedContact[e.target.name] = e.target.value;
                        this.setState({ selectedContact });
                    }}
                    onCheckedChanged={(e) => {
                        const { selectedContact } = this.state;

                        selectedContact[e.target.name] = e.target.checked;
                        this.setState({ selectedContact });
                    }}
                    onContactTypeChanged={(items) => {
                        const { selectedContact } = this.state;
                        selectedContact.contactTypes = (items || []).map((x) => x.value);
                        this.setState({ selectedContact });
                    }}
                    onCompanyChanged={(item) => {
                        const { selectedContact } = this.state;
                        const id = (item ?? {}).value ?? '';
                        selectedContact.companyId = id;
                        this.setState({ selectedContact });
                    }}
                    onSaveCallback={() => this.onOrganizationContactSaved(false)}
                />

                <OrganizationContactForm
                    ref={this.subcontractorOrganizationContactFormRef}
                    show={this.state.showSubcontractorContactsForm}
                    contact={this.state.selectedSubcontractorContact}
                    readOnly={this.state.contactFormIsReadOnly}
                    allowContactTypeEdit={!quickSale.subcontractorContactId}
                    allowAllContactTypes
                    id="subcontractorContactForm"
                    isDisabled={!quickSale?.contractId || quickSale?.isReadOnly || saving}
                    onClose={() => {
                        this.setState({ showSubcontractorContactsForm: false, contactFormIsReadOnly: false });
                        this.subcontractorOrganizationContactFormRef.current.resetForm();
                        this.setState({ selectedSubcontractorContact: null });
                    }}
                    onChange={(e) => {
                        const { selectedSubcontractorContact } = this.state;

                        selectedSubcontractorContact[e.target.name] = e.target.value;
                        this.setState({ selectedSubcontractorContact });
                    }}
                    onCheckedChanged={(e) => {
                        const { selectedSubcontractorContact } = this.state;

                        selectedSubcontractorContact[e.target.name] = e.target.checked;
                        this.setState({ selectedSubcontractorContact });
                    }}
                    onContactTypeChanged={(items) => {
                        const { selectedSubcontractorContact } = this.state;

                        selectedSubcontractorContact.contactTypes = (items || []).map((x) => x.value);
                        this.setState({ selectedSubcontractorContact });
                    }}
                    onCompanyChanged={(item) => {
                        const { selectedSubcontractorContact } = this.state;
                        const id = (item ?? {}).value ?? '';
                        selectedSubcontractorContact.companyId = id;
                        this.setState({ selectedSubcontractorContact });
                    }}
                    onSaveCallback={() => this.onOrganizationContactSaved(true)}
                />

                {/* No county capture for now, should it be here? */}
                <AddressFormNew
                    id="quickSaleAddressForm"
                    ref={this.addressFormRef}
                    countryId={quickSaleContract?.countryId}
                    countryCode={quickSaleContract?.countryCode}
                    onSaveCallback={this.onLocationSaved}
                    namePrefix=""
                />
            </>
        );
    }
}
